/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { css, StyleSheet } from 'aphrodite';
import React, { useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { colors, LoadingSpinner, mixins } from '@blend/consumer-components';

import { mediaBreakpoints } from '../../../common/responsive';
import { mobileDocSubheaderHeight } from '../Signing/DocumentViewer/MobileDocSubheader';

import './PdfViewer.scss';

// react-pdf relies on the following worker script to render PDFs.
// The script renders PDFs in a separate thread so page performance is not impacted.
// We need to manually pull this in, otherwise the request for the script 404s
// and PDFs cannot render.

// https://stackoverflow.com/a/69625304
pdfjs.GlobalWorkerOptions.workerSrc = '/assets/js/pdf.worker.min.v2.9.359.js';

export namespace PdfViewer {
  export interface Props {
    fileUrl: string;
    width: number;
  }
}

const styles = StyleSheet.create({
  document: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    margins: mixins.margin(8),
  },
  page: {
    ...mixins.margin(8, 'auto'),
    [mediaBreakpoints.mobile]: {
      ...mixins.margin(16, 8),
    },
    ':last-child': {
      marginTop: 0,
    },
  },
  loadingWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
  pdfViewer: {
    height: '100%',
    width: '100%',
    [mediaBreakpoints.mobile]: {
      marginTop: mobileDocSubheaderHeight + 8,
    },
  },
});

export const PdfViewer: React.FC<PdfViewer.Props> = ({ fileUrl, width }) => {
  const [numPages, setNumPages] = useState(1);

  function onDocumentLoadSuccess(pdf: pdfjs.PDFDocumentProxy) {
    setNumPages(pdf.numPages);
  }

  function renderLoadingSpinner() {
    return (
      <div className={css(styles.loadingWrapper)}>
        <LoadingSpinner size={'sm'} color={colors.catalyst} />
      </div>
    );
  }

  return (
    <div className={css(styles.pdfViewer)} data-it="pdf-viewer">
      <TransformWrapper wheel={{ wheelDisabled: true, step: 0.1 }} panning={{ disabled: true }}>
        <TransformComponent
          wrapperStyle={{ overflow: 'auto', width: '100%', height: '100%' }}
          contentStyle={{
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Document
            className={css(styles.document)}
            file={fileUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={renderLoadingSpinner}
          >
            {Array.from(new Array(numPages), (_, i) => (
              <Page key={`page_${i + 1}`} width={width - 20} className={css(styles.page)} pageNumber={i + 1} />
            ))}
          </Document>
        </TransformComponent>
      </TransformWrapper>
    </div>
  );
};
